import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, MoreVertical, Share2, Trash2 } from 'lucide-react';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import InviteAdminModal from '@/components/modals/InviteAdminModal';
import EditAdminModal from '@/components/modals/EditAdminModal';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  role: string;
  roleBadgeColor: string;
  lastActive: string;
  status: 'Active' | 'Inactive';
  createdDate: string;
}

const ROLE_BADGE_COLORS: Record<string, string> = {
  'admin': 'bg-red-100 text-red-700',
  'super_admin': 'bg-red-100 text-red-700',
  'operations': 'bg-blue-100 text-blue-700',
  'finance': 'bg-green-100 text-green-700',
  'compliance': 'bg-purple-100 text-purple-700',
};

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('All Roles');
  const [selectedStatus, setSelectedStatus] = useState('All Status');
  const [checkedUsers, setCheckedUsers] = useState<Set<string>>(new Set());
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({ total: 0, active: 0, inactive: 0, roles: 0 });

  useEffect(() => {
    refreshAdminUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (audit 2026-06-12) — pulled out of useEffect so the header refresh
  // button (⟲) can re-trigger the fetch.
  const refreshAdminUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.request('/admin/users', 'GET');
      if (response.success && Array.isArray(response.data)) {
        const mapped: AdminUser[] = response.data.map((u: any) => ({
          id: String(u.id),
          name: u.name || u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
          email: u.email,
          phone: u.phone || u.contact_phone || '—',
          avatar: u.gender === 'female' ? '👩‍💼' : '👨‍💼',
          role: (u.role || 'admin').replace(/_/g, ' ').toUpperCase(),
          roleBadgeColor: ROLE_BADGE_COLORS[u.role] || 'bg-gray-100 text-gray-700',
          lastActive: u.last_login ? new Date(u.last_login).toLocaleString() : '—',
          status: u.is_active !== false ? 'Active' : 'Inactive',
          createdDate: u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
        }));
        setAdminUsers(mapped);
        const active = mapped.filter(u => u.status === 'Active').length;
        const roles = new Set(mapped.map(u => u.role)).size;
        setMetrics({ total: mapped.length, active, inactive: mapped.length - active, roles });
      }
    } catch (error) {
      console.error('Failed to fetch admin users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = adminUsers.filter(u => {
    const matchSearch = !searchTerm || u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchRole = selectedRole === 'All Roles' || u.role === selectedRole.toUpperCase();
    const matchStatus = selectedStatus === 'All Status' || u.status === selectedStatus;
    return matchSearch && matchRole && matchStatus;
  });

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setCheckedUsers(new Set(filteredUsers.map(u => u.id)));
    } else {
      setCheckedUsers(new Set());
    }
  };

  const handleSelectUser = (userId: string) => {
    const newSet = new Set(checkedUsers);
    if (newSet.has(userId)) {
      newSet.delete(userId);
    } else {
      newSet.add(userId);
    }
    setCheckedUsers(newSet);
  };

  // Handler: Invite new admin user
  const handleInviteAdmin = async () => {
    setShowInviteModal(true);
  };

  // Handler: Confirm invite admin
  const handleConfirmInviteAdmin = async (email: string, role: string) => {
    if (!email || !role) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const response = await apiService.request('/admin/users/invite', 'POST', {
        email: email,
        role: role,
        invitedBy: localStorage.getItem('adminId'),
        invitedAt: new Date().toISOString()
      });
      
      if (response.success) {
        alert('✓ Invitation sent successfully');
        setShowInviteModal(false);
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Invite admin error:', error);
      alert('Failed to send invitation: ' + error.message);
    }
  };

  // Handler: Edit admin user
  const handleEditAdmin = async (adminId: string) => {
    const admin = filteredUsers.find(u => u.id === adminId);
    if (!admin) {
      alert('Admin user not found');
      return;
    }
    setEditingAdmin(admin);
  };

  // Handler: Confirm edit admin
  const handleConfirmEditAdmin = async (role: string, email: string) => {
    if (!editingAdmin || !role) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const response = await apiService.request(`/admin/users/${editingAdmin.id}/edit`, 'PUT', {
        role: role,
        email: email,
        modifiedBy: localStorage.getItem('adminId'),
        modifiedAt: new Date().toISOString()
      });
      
      if (response.success) {
        alert('✓ Admin user updated successfully');
        setEditingAdmin(null);
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Edit admin error:', error);
      alert('Failed to update admin: ' + error.message);
    }
  };

  // Handler: Explore audit logs
  const handleExploreAuditLogs = async () => {
    try {
      const response = await apiService.request('/admin/audit-logs', 'GET', {
        limit: 100
      });
      
      if (response.success && response.data) {
        alert(`✓ Found ${response.data.length || 0} audit log entries`);
        navigate('/admin/audit-logs');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Audit logs error:', error);
      alert('Failed to fetch audit logs: ' + error.message);
    }
  };

  // Handler: Manage roles
  const handleManageRoles = async () => {
    try {
      const response = await apiService.request('/admin/roles/list', 'GET');
      
      if (response.success && response.data) {
        alert(`✓ Managing ${response.data.length || 0} roles`);
        navigate('/admin/roles-management');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Manage roles error:', error);
      alert('Failed to fetch roles: ' + error.message);
    }
  };

  // Handler: View global policy
  const handleViewGlobalPolicy = async () => {
    try {
      const response = await apiService.request('/admin/policies/global', 'GET');
      
      if (response.success && response.data) {
        alert('✓ Global policy loaded');
        navigate('/admin/global-policies');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('View policy error:', error);
      alert('Failed to fetch policy: ' + error.message);
    }
  };

  return (
    <AdminLayout>
      <div className="w-full bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Settings</span>
              <span className="text-gray-300">›</span>
              <span className="font-semibold text-gray-900">ADMIN USERS</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                4 PERENNIAL YAL
              </div>
              <button className="text-gray-400 hover:text-gray-600">
                <Search className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/admin/notifications')}
                title="Notifications"
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="w-5 h-5 relative flex items-center justify-center">🔔</span>
              </button>
              <button
                onClick={refreshAdminUsers}
                disabled={loading}
                title="Refresh"
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <span className={`w-5 h-5 relative flex items-center justify-center ${loading ? 'animate-spin' : ''}`}>⟲</span>
              </button>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-auto">
          <div className="px-6 py-8">
            {/* Page Title */}
            <div className="mb-8 flex items-end justify-between">
              <div>
                <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">SETTINGS › ADMIN USERS</p>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Users</h1>
                <p className="text-gray-600">Manage platform administrator accounts and roles</p>
              </div>
              <button 
                onClick={() => setShowInviteModal(true)}
                className="bg-teal-700 hover:bg-teal-800 text-white font-semibold px-6 py-2.5 rounded-lg flex items-center gap-2">
                Invite Admin
              </button>
            </div>

            {/* Metrics Cards */}
            <div className="grid grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Total Admins</p>
                <p className="text-4xl font-bold text-gray-900">{loading ? '—' : metrics.total}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Active</p>
                <p className="text-4xl font-bold text-gray-900">{loading ? '—' : metrics.active}</p>
                <p className="text-xs text-gray-400 mt-1">Last 30 days</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Inactive</p>
                <p className="text-4xl font-bold text-gray-900">{loading ? '—' : String(metrics.inactive).padStart(2, '0')}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Roles</p>
                <p className="text-4xl font-bold text-gray-900">{loading ? '—' : String(metrics.roles).padStart(2, '0')}</p>
                <p className="text-xs text-gray-400 mt-1">Distinct Types</p>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex gap-3 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 flex items-center gap-2"
              >
                <option>All Roles</option>
                <option>Super Admin</option>
                <option>Operations Manager</option>
                <option>Finance Admin</option>
                <option>Compliance Reviewer</option>
              </select>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 flex items-center gap-2"
              >
                <option>All Status</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-lg border border-gray-200 mb-8">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={filteredUsers.length > 0 && checkedUsers.size === filteredUsers.length}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Admin</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role Badge</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Last Active</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="px-6 py-8 text-center text-gray-400">Loading admin users...</td></tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr><td colSpan={9} className="px-6 py-8 text-center text-gray-400">No admin users found</td></tr>
                  ) : null}
                  {filteredUsers.map((user) => (
                    <tr 
                      key={user.id} 
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                    >  <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={checkedUsers.has(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-lg">
                            {user.avatar}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{user.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{user.phone}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${user.roleBadgeColor}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{user.lastActive}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${user.status === 'Active' ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span className="text-sm font-medium text-gray-900">{user.status}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{user.createdDate}</td>
                      <td className="px-6 py-4">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingAdmin(user);
                          }}
                          className="text-gray-400 hover:text-gray-600">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* (audit 2026-06-12) — admin user lists are small enough to fit
                on a single page; Previous / Next were inert decoration. They
                stay rendered but disabled to make that clear. */}
            <div className="flex items-center justify-between mb-8">
              <p className="text-sm text-gray-600">Showing {filteredUsers.length} of {metrics.total} administrators</p>
              <div className="flex items-center gap-2">
                <button
                  disabled
                  className="px-4 py-2 font-medium text-gray-400 text-sm cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  disabled
                  className="px-4 py-2 font-medium text-gray-400 text-sm cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-3 gap-6">
              {/* Audit History Card */}
              <div className="bg-gradient-to-br from-teal-700 to-teal-900 rounded-lg p-6 text-white">
                <p className="text-xs uppercase font-bold tracking-wider mb-2 opacity-80">AUDIT HISTORY</p>
                <h3 className="text-2xl font-bold mb-2">View complete admin activity logs</h3>
                <p className="text-sm text-teal-100 mb-6">Track all changes made by administrators for compliance</p>
                <button
                  onClick={() => navigate('/admin/configuration-status?view=audit-log')}
                  className="uppercase text-sm font-bold text-teal-300 hover:text-white flex items-center gap-2"
                >
                  EXPLORE LOGS
                  <span>›</span>
                </button>
              </div>

              {/* Role Management Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-lg">✓</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Role Management</h3>
                <p className="text-sm text-gray-600 mb-6">Configure permissions and access levels for system roles.</p>
                <button
                  onClick={() => navigate('/admin/permissions')}
                  className="uppercase text-sm font-bold text-teal-700 hover:text-teal-900"
                >
                  MANAGE ROLES
                </button>
              </div>

              {/* Security Policy Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-lg">🔒</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Security Policy</h3>
                <p className="text-sm text-gray-600 mb-6">Enforce 2FA and session timeouts for all admin accounts.</p>
                <button
                  onClick={() => navigate('/admin/platform-rules?focus=security')}
                  className="uppercase text-sm font-bold text-teal-700 hover:text-teal-900"
                >
                  GLOBAL POLICY
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <InviteAdminModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} />
      <EditAdminModal isOpen={editingAdmin !== null} onClose={() => setEditingAdmin(null)} admin={editingAdmin} />
    </AdminLayout>
  );
};

export default AdminUsersPage;
